import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import {
  getFormattedFiatCurrencyNumber,
  bemCssModules,
  getTestId,
  trimStringMiddle,
} from '@src/utils';
import style from '@styles/_const.scss';
import { useWindowSize, useRedirectToDefaultCurrency, useNavigate } from '@utils/hooks';

import { walletActions, useStore } from '@modules';
import { CryptoCurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';

import AddressBlock from '@components/AddressBlock';
import { Transactions } from '@containers/Transactions';
import LoaderLayout from '@layouts/LoaderLayout';
import WalletLayout from '@layouts/WalletLayout';

import BalanceInfo from './BalanceInfo';
import Buttons from './Buttons';
import Value from './Value';

import styles from './Balance.scss';

const cn = bemCssModules(styles, 'Balance');
const ti = getTestId('Balance');

const Balance: CustomFC = ({ className }) => {
  const [{ global, wallet, transactions }, dispatch] = useStore();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const currency = useRedirectToDefaultCurrency();

  const isLoading =
    (wallet.loaders.getWallet && !wallet.data) ||
    (wallet.loaders.getTopupAddress && !wallet.topupAddressData);
  const address = wallet.topupAddressData[currency]?.address;
  const balanceData = wallet.data?.[currency];
  const currencyConfig = global.currencies.config.display_options[currency];
  const isDesktop = width > parseInt(style.tablet, 10);
  const hasTransactions = !!Object.keys(transactions.list).length;
  const isLoadingGetTransactions = transactions.loaders.getTransactions;
  const hasBalanceData = Number(balanceData?.fiat_balance) > 0;
  const currencyName = currencyConfig?.fullname || currency;

  const header = (
    <h1 className={cn('header')}>
      {currencyName.length > 10 ? trimStringMiddle(currencyName, 16) : currencyName}
    </h1>
  );

  const fiatBalance = getFormattedFiatCurrencyNumber({
    amount: Number(balanceData?.fiat_balance) || 0,
    currency: balanceData?.fiat_currency || FiatCurrencyEnum.EUR,
  });

  useEffect(() => {
    dispatch(walletActions.getWallet());
  }, []);

  useEffect(() => {
    dispatch(walletActions.getTopupAddress(currency));
  }, [currency]);

  const onClickBackButton = () => navigate(CONST.nav.wallets);

  return (
    <WalletLayout header={header} hasSidebar={isDesktop} backButton={onClickBackButton}>
      <div className={cn(null, [className])} data-testid={ti()}>
        <LoaderLayout isLoading={isLoading}>
          <Value className={cn('value')} currency={currency} />
          {hasBalanceData && <BalanceInfo fiat={fiatBalance} />}

          <Transactions title={t('transaction.latest_transactions')} currency={currency} />
          {!hasTransactions && !isLoadingGetTransactions && (
            <>
              {currency === CryptoCurrencyEnum.ETH && (
                <div className={cn('warning')}>{t('tip.deposit.eth')}</div>
              )}
              <AddressBlock address={address} />
            </>
          )}
          <Buttons currency={currency} />
        </LoaderLayout>
      </div>
    </WalletLayout>
  );
};

export default Balance;
